import { useEffect, useState } from 'react'
import {
  classButtonPlaceholders,
  degreesItems,
  classModalTitles,
  classModalTitlesSimplified,
  ModalTypes
} from '../constants'

import {
  createEntityMutation,
  editEntityMutation,
  deleteEntityMutation
} from 'api/mutations'
import { post_event } from 'utils/analytics'
import { useToast } from 'utils/hooks/use-toast/useToast'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { Degree } from 'api/data.types'

const getDefaultDegreeForEntity = (
  parentEntityType?: string,
  doActivityV2?: boolean
): string | undefined => {
  if (doActivityV2 && parentEntityType !== 'school') {
    return Degree.TEACHER
  }

  return undefined
}

type SelectorItem = {
  id: string
  text: string
}

const useClassModal = ({
  type,
  client,
  entityId,
  parentId,
  parentEntityType,
  defaultEntityName,
  defaultEntityDegree
}) => {
  const [modalTitle, setModalTitle] = useState<string>('')
  const [buttonLabel, setButtonLabel] = useState<string>('')
  const [selectorItems, setSelectorItems] = useState<SelectorItem[] | null>()
  const [scholarYear, setScholarYear] = useState<string>(defaultEntityDegree)
  const [entityName, setEntityName] = useState<string>(defaultEntityName)
  const [canSubmitEntity, setCanSubmitEntity] = useState<boolean>()

  const { toastMessage } = useToast()
  const { featureToggles } = useFeatureToggles()
  const { user } = useUserInfo()

  useEffect(() => {
    setSelectorItems(degreesItems)

    if (user?.featureToggles?.doActivityV2) {
      setModalTitle(classModalTitlesSimplified[type])
    } else {
      setModalTitle(classModalTitles[type])
    }

    setButtonLabel(classButtonPlaceholders[type])
  }, [type, user?.featureToggles?.doActivityV2])

  useEffect(() => {
    const isScholarYearRequired = !(
      featureToggles?.doActivityV2 && parentEntityType !== 'school'
    )
    const hasRequiredFields = isScholarYearRequired
      ? scholarYear && entityName
      : entityName

    setCanSubmitEntity(Boolean(hasRequiredFields || type === ModalTypes.delete))
  }, [
    type,
    scholarYear,
    entityName,
    featureToggles?.doActivityV2,
    parentEntityType
  ])

  const handleEntityName = (name: string) => {
    setEntityName(name)
  }

  const handleEntitySchollarYear = (degree: string) => {
    setScholarYear(degree)
  }

  const editClass = () => {
    const post_event_value = {
      entity_id: entityId,
      new_info: entityName,
      old_info: defaultEntityName
    }

    const degreeToSend =
      scholarYear ||
      getDefaultDegreeForEntity(parentEntityType, featureToggles?.doActivityV2)

    client
      .mutate({
        mutation: editEntityMutation,
        variables: {
          degree: degreeToSend,
          entityId: entityId,
          name: entityName
        }
      })
      .then(() => {
        post_event(
          `turmas-e-usuarios/edit_entity_info`,
          JSON.stringify(post_event_value)
        )
        window.location.reload()
      })
      .catch(err =>
        toastMessage({
          type: 'error',
          message: `Oops, aconteceu um erro: ${err.message}`
        })
      )
  }

  const createClass = () => {
    const degreeToSend =
      scholarYear ||
      getDefaultDegreeForEntity(parentEntityType, featureToggles?.doActivityV2)

    client
      .mutate({
        mutation: createEntityMutation,
        variables: {
          degree: degreeToSend,
          parentEntityId: parentId,
          name: entityName
        }
      })
      .then(() => window.location.reload())
      .catch(err => {
        if (err.message === 'invalid_entity_type') {
          toastMessage({
            type: 'error',
            message:
              'Você não pode criar uma turma dentro desta entidade. Entre em contato com atendimento@arvore.com.br'
          })
          return
        }

        toastMessage({
          type: 'error',
          message: `Oops, aconteceu um erro: ${err.message}`
        })
      })
  }

  const deleteClass = () => {
    client
      .mutate({
        mutation: deleteEntityMutation,
        variables: {
          entityId: entityId
        }
      })
      .then(() => window.location.reload())
      .catch(err =>
        toastMessage({
          type: 'error',
          message: `Oops, aconteceu um erro: ${err.message}`
        })
      )
  }

  const handleSubmit = () => {
    const submitFunction = {
      edit: editClass,
      create: createClass,
      delete: deleteClass
    }[type]
    submitFunction()
  }

  return {
    modalTitle,
    buttonLabel,
    selectorItems,
    canSubmitEntity,
    handleSubmit,
    handleEntityName,
    handleEntitySchollarYear
  }
}

export { useClassModal }
